import Phaser from 'phaser';
import { SCENE_KEYS } from '../config';
import { AssetGenerator } from '../rendering/AssetGenerator';

export class PreloadScene extends Phaser.Scene {
  private gen = new AssetGenerator(this);

  constructor() { super({ key: SCENE_KEYS.PRELOAD }); }

  preload(): void {
    const cw = this.cameras.main.width;
    const ch = this.cameras.main.height;

    const box = this.add.graphics();
    (box as any).fillStyle(0x000000, 0.8);
    (box as any).fillRect(cw / 2 - 200, ch / 2 - 40, 400, 80);

    const label = this.add.text(cw / 2, ch / 2 - 60, 'CARGANDO CENTRO HABANA...', {
      fontSize: '14px', fontFamily: 'monospace', color: '#ffffff',
    }).setOrigin(0.5);

    const bar = this.add.graphics();
    this.load.on('progress', (value: number) => {
      bar.clear();
      (bar as any).fillStyle(0x44aa44, 1);
      (bar as any).fillRect(cw / 2 - 190, ch / 2 - 25, 380 * value, 20);
    });

    this.load.on('complete', () => {
      bar.destroy();
      box.destroy();
      label.destroy();

      // Generate all procedural assets AFTER external images are in the cache.
      // generateAll() checks textures.exists() so it won't overwrite DrawThings PNGs.
      (this.gen as AssetGenerator).generateAll();

      this.scene.start(SCENE_KEYS.MENU);
    });

    // Register external background assets (actual DrawThings output dimensions)
    const base = 'assets/background/';
    this.gen.registerExternalSprite('sky', base + 'sky-2048x384.png');
    this.gen.registerExternalSprite('clouds-v1', base + 'clouds-v1-2048x384.png');
    this.gen.registerExternalSprite('clouds-v2', base + 'clouds-v2-2048x384.png');
    this.gen.registerExternalSprite('buildings-far-v1', base + 'buildings-far-v1-2048x384.png');
    this.gen.registerExternalSprite('buildings-far-v2', base + 'buildings-far-v2-2048x384.png');
    this.gen.registerExternalSprite('buildings-far-v3', base + 'buildings-far-v3-2048x384.png');
    this.gen.registerExternalSprite('buildings-far-v4', base + 'buildings-far-v4-2048x384.png');
    this.gen.registerExternalSprite('buildings-mid', base + 'buildings-mid-2048x384.png');
    this.gen.registerExternalSprite('buildings-close', base + 'buildings-close-2048x384.png');
    this.gen.registerExternalSprite('platform-ground', base + 'platform-ground.png');
    this.gen.registerExternalSprite('platform-1', base + 'platform-1.png');
    this.gen.registerExternalSprite('platform-2', base + 'platform-2.png');
    this.gen.registerExternalSprite('platform-3', base + 'platform-3.png');
    this.gen.registerExternalSprite('platform-4', base + 'platform-4.png');
    this.gen.registerExternalSprite('sea-tile', base + 'sea-tile-512x512.png');

    // Checkpoint assets — generated by scripts/generate_assets.py (final res in name)
    const cpBase = 'assets/checkpoints/';
    this.gen.registerExternalSprite('checkpoint-1', cpBase + 'checkpoint-v1-40x80.png');
    this.gen.registerExternalSprite('checkpoint-2', cpBase + 'checkpoint-v2-40x80.png');
    this.gen.registerExternalSprite('checkpoint-3', cpBase + 'checkpoint-v3-40x80.png');
    this.gen.registerExternalSprite('checkpoint-4', cpBase + 'checkpoint-v4-40x80.png');

    // Powerup assets — generated by scripts/generate_assets.py (final res in name)
    const puBase = 'assets/powerups/';
    this.gen.registerExternalSprite('powerup_euro', puBase + 'euro-coin-64x64.png');
    this.gen.registerExternalSprite('powerup_dollar', puBase + 'dollar-bill-72x48.png');
    this.gen.registerExternalSprite('powerup_peso', puBase + 'peso-coin-48x48.png');
    this.gen.registerExternalSprite('powerup_croqueta', puBase + 'croqueta-40x32.png');
    this.gen.registerExternalSprite('powerup_mlc', puBase + 'mlc-card-72x56.png');
    this.gen.registerExternalSprite('powerup_cafecito', puBase + 'cafecito-40x56.png');
    this.gen.registerExternalSprite('powerup_ecoflow', puBase + 'ecoflow-56x56.png');
    this.gen.registerExternalSprite('powerup_solar-panel', puBase + 'solar-panel-64x48.png');
    this.gen.registerExternalSprite('powerup_gas-can', puBase + 'gas-can-48x56.png');
    this.gen.registerExternalSprite('powerup_guarapo', puBase + 'guarapo-48x64.png');
    this.gen.registerExternalSprite('powerup_coffee-la-llave', puBase + 'coffee-la-llave-40x56.png');
    this.gen.registerExternalSprite('powerup_cohiba-cigar-box', puBase + 'cohiba-cigar-box-56x40.png');
    this.gen.registerExternalSprite('powerup_cristal-beer-6pack', puBase + 'cristal-beer-6pack-64x48.png');
    this.gen.registerExternalSprite('powerup_cuban-ration-booklet', puBase + 'cuban-ration-booklet-56x40.png');
    this.gen.registerExternalSprite('powerup_havana-club-bottle', puBase + 'havana-club-bottle-48x64.png');
    this.gen.registerExternalSprite('powerup_malta-guajira-bottle', puBase + 'malta-guajira-bottle-48x64.png');
    this.gen.registerExternalSprite('powerup_pan-con-lechon', puBase + 'pan-con-lechon-72x48.png');
    this.gen.registerExternalSprite('powerup_pasaporte', puBase + 'pasaporte-48x64.png');
    this.gen.registerExternalSprite('powerup_soy-oil-bottle', puBase + 'soy-oil-bottle-40x56.png');
    this.gen.registerExternalSprite('powerup_egg-cartons', puBase + 'egg-cartons-64x48.png');

    // NPC assets — generated by scripts/generate_assets.py (final res in name)
    // Keys match the design file names (kebab-case).
    const npcBase = 'assets/npcs/';
    this.gen.registerExternalSprite('npc_american-tourist', npcBase + 'american-tourist-sprite-85x135.png');
    this.gen.registerExternalSprite('npc_pionero-child', npcBase + 'cuban-pionero-child-85x135.png');
    this.gen.registerExternalSprite('npc_curly-bun-woman', npcBase + 'curly-bun-cuban-woman-85x135.png');
    this.gen.registerExternalSprite('npc_trovador', npcBase + 'cuban-trovador-guitar-85x135.png');
    this.gen.registerExternalSprite('npc_peanut-vendor', npcBase + 'cuban-peanut-vendor-85x135.png');
    this.gen.registerExternalSprite('npc_elderly-lady', npcBase + 'elderly-cuban-lady-85x135.png');
    // Extra NPC variants (used via per-spawn spriteKey override)
    this.gen.registerExternalSprite('npc_boy-baseball', npcBase + 'cuban-boy-baseball-85x135.png');
    this.gen.registerExternalSprite('npc_boy-soccer', npcBase + 'cuban-boy-soccer-85x135.png');
    this.gen.registerExternalSprite('npc_kid-running', npcBase + 'cuban-kid-running-sprite-85x135.png');
    this.gen.registerExternalSprite('npc_waiter', npcBase + 'cuban-waiter-four-beers-85x135.png');
    this.gen.registerExternalSprite('npc_cuban-man', npcBase + 'cuban-man-character-85x135.png');
    this.gen.registerExternalSprite('npc_woman-walking', npcBase + 'cuban-woman-walking-85x135.png');
    this.gen.registerExternalSprite('npc_panfilo', npcBase + 'panfilo-cuban-sprite-85x135.png');
    this.gen.registerExternalSprite('npc_chulo', npcBase + 'chulo-icon-85x135.png');
    this.gen.registerExternalSprite('npc_hustler', npcBase + 'cuban-hustler-85x135.png');
    this.gen.registerExternalSprite('npc_money-man', npcBase + 'man-counting-money-85x135.png');

    // Enemy assets — generated by scripts/generate_assets.py (final res in name)
    const enBase = 'assets/enemies/';
    this.gen.registerExternalSprite('enemy_pcc', enBase + 'cuban-pnr-officer-sprite-95x135.png');
    this.gen.registerExternalSprite('enemy_fmc', enBase + 'inspector-flaco-95x135.png');
    this.gen.registerExternalSprite('enemy_ujc', enBase + 'cuban-soldier-cartoon-95x135.png');
    // Extra enemy variants (used via per-spawn spriteKey override)
    this.gen.registerExternalSprite('enemy_inspector-gordo', enBase + 'inspector-gordo-95x135.png');
    this.gen.registerExternalSprite('enemy_skinny-pnr', enBase + 'skinny-pnr-officer-cartoon-95x135.png');
    this.gen.registerExternalSprite('enemy_abuela-angry', enBase + 'abuela-angry-95x135.png');
    this.gen.registerExternalSprite('enemy_pcc-facundo', enBase + 'pcc-facundo-95x135.png');
    this.gen.registerExternalSprite('enemy_ujc-mature', enBase + 'ujc-mature-95x135.png');
    this.gen.registerExternalSprite('enemy_cartoon-singer', enBase + 'cartoon-singer-95x135.png');
    this.gen.registerExternalSprite('enemy_reggaeton-singer', enBase + 'reggaeton-singer-95x135.png');
    this.gen.registerExternalSprite('enemy_sad-street-dog', enBase + 'sad-street-dog-95x135.png');

    // Player spritesheet v2 (re-packed, uniform 128×128 cells, 8×5 grid)
    this.load.spritesheet('player-sheet-v1', 'assets/player/player-sheet-v2.png', {
      frameWidth: 128,
      frameHeight: 128,
    });

    // Background music loops (scripts/generate_music*.py)
    this.load.audio('music-level1', 'assets/audio/music-loop.wav');
    this.load.audio('music-dembow', 'assets/audio/music-dembow.wav');
    this.load.audio('music-guajira', 'assets/audio/music-guajira.wav');

    // Load external assets first; 'complete' fires when ALL images are loaded
    (this.gen as AssetGenerator).loadExternalAssets(this);
  }
}
